import { type FC, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { DateRangePicker } from "./date_range_picker";
import { TimeRangeButtons } from "./time_range_buttons";
import { defaultDateRange } from "../lib/date_range";
import { IssuanceTimelineChart } from "./issuance_timeline_chart";
import { IssuanceGroupChart } from "./issuance_group_chart";
import { IssuancePolicyChart } from "./issuance_policy_chart";
import {
  useIssuanceData,
  useIssuanceLoading,
  useIssuanceError,
  fetchIssuanceData,
} from "../stores/issuance_store";
import { useDeviceGroups } from "../stores/device_group_store";
import { useCertificatePolicies } from "../stores/certificate_policy_store";
import {
  bucketByTime,
  granularityForRange,
  groupByKey,
} from "../../../shared/issuance_utils";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const ChartRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: var(--vmx-white);
  border: var(--vmx-border);
  border-radius: var(--vmx-radius);
  padding: 20px;
  min-width: 0;
  overflow: hidden;
`;

const ChartTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--vmx-gray-900);
  margin: 0;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ChartCount = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--vmx-gray-500);
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid var(--vmx-gray-200);
  border-top-color: var(--vmx-accent);
  animation: vmx-spin 0.8s linear infinite;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 300px;
  text-align: center;
`;

const ErrorMessage = styled.p`
  font-family: var(--vmx-font);
  font-size: 0.875rem;
  color: var(--vmx-danger);
  margin: 0;
`;

function daysBetween(start: string, end: string): number {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(1, Math.round(ms / 86_400_000));
}

export const IssuanceGraphSection: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Derive date range from URL params, falling back to defaults
  const defaults = defaultDateRange();
  const startDate = searchParams.get("startDate") ?? defaults.startDate;
  const endDate = searchParams.get("endDate") ?? defaults.endDate;

  const setDateRange = (range: { startDate: string; endDate: string }) => {
    const next = new URLSearchParams(searchParams);
    next.set("startDate", range.startDate);
    next.set("endDate", range.endDate);
    setSearchParams(next, { replace: true });
  };

  const loading = useIssuanceLoading();
  const error = useIssuanceError();
  const certificates = useIssuanceData();
  const groups = useDeviceGroups();
  const policies = useCertificatePolicies();

  useEffect(() => {
    void fetchIssuanceData(startDate, endDate);
  }, [startDate, endDate]);

  const groupNameMap = useMemo(() => {
    const m = new Map<string, string>();
    groups.forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const policyNameMap = useMemo(() => {
    const m = new Map<string, string>();
    policies.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [policies]);

  const days = useMemo(
    () => daysBetween(startDate, endDate),
    [startDate, endDate]
  );

  const timelineData = useMemo(() => {
    if (!certificates) return [];
    const granularity = granularityForRange(days);
    return bucketByTime(certificates, granularity, startDate, endDate);
  }, [certificates, days, startDate, endDate]);

  const groupData = useMemo(
    () =>
      certificates
        ? groupByKey(certificates, "deviceGroupId", groupNameMap)
        : [],
    [certificates, groupNameMap]
  );

  const policyData = useMemo(
    () =>
      certificates
        ? groupByKey(certificates, "certificatePolicyId", policyNameMap)
        : [],
    [certificates, policyNameMap]
  );

  const handleRetry = () => {
    void fetchIssuanceData(startDate, endDate);
  };

  const totalCerts = certificates?.length ?? 0;

  const handleTimeRangeSelect = (
    _rangeKey: string,
    start: string,
    end: string
  ) => {
    setDateRange({ startDate: start, endDate: end });
  };

  const handleManualDateChange = (range: {
    startDate: string;
    endDate: string;
  }) => {
    setDateRange(range);
  };

  return (
    <Section>
      <ControlsRow>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleManualDateChange}
        />
        <TimeRangeButtons
          startDate={startDate}
          endDate={endDate}
          onSelect={handleTimeRangeSelect}
        />
      </ControlsRow>

      <ChartCard>
        <ChartHeader>
          <ChartTitle>Issuance Timeline</ChartTitle>
          {!loading && error === undefined && (
            <ChartCount>{totalCerts} certificates</ChartCount>
          )}
        </ChartHeader>
        {loading ? (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        ) : error !== undefined ? (
          <ErrorWrapper>
            <ErrorMessage>{error}</ErrorMessage>
            <button
              type="button"
              className="vmx-btn vmx-btn-sm"
              onClick={handleRetry}
            >
              Retry
            </button>
          </ErrorWrapper>
        ) : (
          <IssuanceTimelineChart
            data={timelineData}
            onBarClick={(bucket) =>
              navigate(
                `/certificates?issuedFrom=${bucket.startDate}&issuedTo=${bucket.endDate}`
              )
            }
          />
        )}
      </ChartCard>

      <ChartRow>
        <ChartCard>
          <ChartHeader>
            <ChartTitle>By Device Group</ChartTitle>
            {!loading && error === undefined && (
              <ChartCount>{totalCerts} certificates</ChartCount>
            )}
          </ChartHeader>
          {loading ? (
            <SpinnerWrapper>
              <Spinner />
            </SpinnerWrapper>
          ) : error !== undefined ? (
            <ErrorWrapper>
              <ErrorMessage>{error}</ErrorMessage>
              <button
                type="button"
                className="vmx-btn vmx-btn-sm"
                onClick={handleRetry}
              >
                Retry
              </button>
            </ErrorWrapper>
          ) : (
            <IssuanceGroupChart
              data={groupData}
              onSegmentClick={(id) =>
                navigate(
                  `/certificates?groupId=${id}&issuedFrom=${startDate}&issuedTo=${endDate}`
                )
              }
            />
          )}
        </ChartCard>
        <ChartCard>
          <ChartHeader>
            <ChartTitle>By Certificate Policy</ChartTitle>
            {!loading && error === undefined && (
              <ChartCount>{totalCerts} certificates</ChartCount>
            )}
          </ChartHeader>
          {loading ? (
            <SpinnerWrapper>
              <Spinner />
            </SpinnerWrapper>
          ) : error !== undefined ? (
            <ErrorWrapper>
              <ErrorMessage>{error}</ErrorMessage>
              <button
                type="button"
                className="vmx-btn vmx-btn-sm"
                onClick={handleRetry}
              >
                Retry
              </button>
            </ErrorWrapper>
          ) : (
            <IssuancePolicyChart
              data={policyData}
              onSegmentClick={(id) =>
                navigate(
                  `/certificates?policyId=${id}&issuedFrom=${startDate}&issuedTo=${endDate}`
                )
              }
            />
          )}
        </ChartCard>
      </ChartRow>
    </Section>
  );
};
